import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useAppDispatch } from "hooks";
import {
  Card,
  InlineStack,
  BlockStack,
  Text,
  Button,
  EmptyState,
  Banner,
  Modal,
} from "@shopify/polaris";
import useIsPhone from "features/files/use-is-phone";
import DateRangePicker from "features/datepicker/date-range-picker";
import DataTable from "components/datatable/Datatable";
import DataTableSkeleton from "components/datatable/DataTableSkeleton";
import Currency from "components/format/currency/currency";
import { Column } from "types/Datatable";
import { RootState } from "configureStore";
import {
  fetchAllFuelRecords,
  deleteFuelRecord,
  FuelRecordsTotals,
} from "services/fuel-report.service";
import { fetchFuelReportsThunk } from "containers/fuel-reports/fuel-reports.slice";
import { saveFuelBarcode } from "features/fuel-barcodes/fuelBarcodes.service";
import { FuelBarcodeType } from "features/fuel-barcodes/fuelBarcodes.types";

/** Local YYYY-MM-DD (no UTC shift). */
function toYmd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Default filter = the whole previous calendar month. */
function previousMonthRange(): { start: string; end: string } {
  const now = new Date();
  const firstOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastMonthEnd = new Date(firstOfThisMonth.getTime() - 1);
  const lastMonthStart = new Date(
    lastMonthEnd.getFullYear(),
    lastMonthEnd.getMonth(),
    1
  );
  return { start: toYmd(lastMonthStart), end: toYmd(lastMonthEnd) };
}

/**
 * Same priority as FuelRecordClassificationService.isFuelRecord:
 * barcode → V3 serviceType → productClass.
 */
function getFuelListEntry(
  record: any
): { type: FuelBarcodeType; value: string } | null {
  const barcode = String(record?.barcode ?? "").trim();
  if (barcode) return { type: "barcode", value: barcode };

  if (record?.csvImportType === "V3") {
    const serviceType = String(record?.serviceType ?? "").trim();
    if (serviceType) return { type: "serviceType", value: serviceType };
    return null;
  }

  const productClass = String(record?.productClass ?? "").trim();
  if (productClass) return { type: "productClass", value: productClass };
  return null;
}

function FuelTransactionsTab({ refreshToken = 0 }: { refreshToken?: number }) {
  const { t } = useTranslation();
  const { companyId } = useParams();
  const dispatch = useAppDispatch();
  const isPhone = useIsPhone();
  const { role } = useSelector((state: RootState) => state.authReducer);
  const { filtersValue } = useSelector(
    (state: RootState) => state.fuelReportReducer
  );
  const canManageFuelList =
    !!role?.permissionsgroups?.includes("Master") ||
    !!role?.permissionsgroups?.includes("Access fuel reports");

  const [range, setRange] = useState(previousMonthRange);
  const [records, setRecords] = useState<any[]>([]);
  const [totals, setTotals] = useState<FuelRecordsTotals | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [addingId, setAddingId] = useState<string | null>(null);
  const [recordToDelete, setRecordToDelete] = useState<any | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [actionError, setActionError] = useState("");
  const [actionSuccess, setActionSuccess] = useState("");

  // Sequence requests so a slow response for an old range can't overwrite the
  // results of a newer one after the user changes the filter.
  const reqRef = useRef(0);
  const load = useCallback(async () => {
    if (!companyId || !range.start || !range.end) return;
    const reqId = ++reqRef.current;
    setIsLoading(true);
    setIsError(false);
    try {
      const data = await fetchAllFuelRecords(companyId, {
        dateFrom: range.start,
        dateTo: range.end,
      });
      if (reqId !== reqRef.current) return; // superseded by a newer request
      setRecords(Array.isArray(data?.records) ? data.records : []);
      setTotals(data?.totals || null);
    } catch (e) {
      if (reqId !== reqRef.current) return;
      setIsError(true);
      setRecords([]);
      setTotals(null);
    } finally {
      if (reqId === reqRef.current) setIsLoading(false);
    }
  }, [companyId, range.start, range.end]);

  useEffect(() => {
    load();
  }, [load]);

  // The parent bumps the token after a CSV import. Remembering the handled
  // value keeps mounting (which already loads) and range changes from also
  // triggering this refetch.
  const handledRefreshToken = useRef(refreshToken);
  useEffect(() => {
    if (refreshToken === handledRefreshToken.current) return;
    handledRefreshToken.current = refreshToken;
    load();
  }, [refreshToken, load]);

  const handleAddToFuelList = useCallback(
    async (item: any) => {
      if (!companyId || !item?.fuelListEntry || item.isFuel) return;
      setActionError("");
      setActionSuccess("");
      setAddingId(item.id);
      try {
        const saved = await saveFuelBarcode(companyId, {
          type: item.fuelListEntry.type,
          value: item.fuelListEntry.value,
          description: null,
          isActive: true,
        });
        setActionSuccess(
          saved?.reactivated
            ? t("add_to_fuel_list_reactivated", {
                value: item.fuelListEntry.value,
              })
            : t("add_to_fuel_list_success", {
                value: item.fuelListEntry.value,
              })
        );
        await load();
      } catch (e: any) {
        const code = e?.response?.data;
        if (code === "FUEL_BARCODE_DUPLICATE") {
          setActionError(t("fuel_barcode_duplicate"));
        } else if (
          code === "COMPANY_USER_FUEL_PERMISSION_NOT_FOUND" ||
          code === "COMPANY_USER_MASTER_NOT_FOUND"
        ) {
          setActionError(t("add_to_fuel_list_permission_required"));
        } else {
          setActionError(t("add_to_fuel_list_error"));
        }
      } finally {
        setAddingId(null);
      }
    },
    [companyId, load, t]
  );

  const handleDelete = useCallback(async () => {
    if (!companyId || !recordToDelete) return;
    setActionError("");
    setActionSuccess("");
    setIsDeleting(true);
    try {
      const data = await deleteFuelRecord(companyId, recordToDelete.id);
      const affected = data?.affectedReports?.length || 0;
      setActionSuccess(
        affected > 0
          ? t("delete_fuel_record_reports_recalculated", { count: affected })
          : t("delete_fuel_record_success")
      );
      setRecordToDelete(null);
      await load();
      // The reports tab reads a collection fetched on mount, so it would keep
      // showing the amounts the deleted transaction was part of.
      if (affected > 0 && companyId) {
        dispatch(fetchFuelReportsThunk(companyId, filtersValue));
      }
    } catch (e) {
      setActionError(t("delete_fuel_record_error"));
    } finally {
      setIsDeleting(false);
    }
  }, [companyId, recordToDelete, load, t, dispatch, filtersValue]);

  const tableData = useMemo(
    () =>
      records.map((item: any, idx: number) => ({
        id: item.id,
        "#": idx + 1,
        dateTime: item.dateTime || "-",
        cardUser: item.cardUser || "-",
        cardNumber: item.cardNumber || "-",
        place: item.place || item.station || "-",
        Name: item.Name || item.serviceType || "-",
        quantity: item.quantity ?? "-",
        // Compute net amount from raw columns (same formula as the other
        // FuelReportData tables) rather than trusting a single precomputed field.
        amount:
          (item.amountWithoutDiscount || 0) -
          (item.discountAmount || 0) -
          (item.receiptFees || 0) +
          (item.discountsOnTaxes || 0),
        recordType: item.isFuel ? t("type_fuel") : t("type_additional"),
        tone: item.isFuel ? "success" : "attention",
        isFuel: !!item.isFuel,
        fuelListEntry: getFuelListEntry(item),
      })),
    [records, t]
  );

  // A phone card has four slots. Those who manage the fuel list need the
  // add/delete buttons more than the place, so the buttons take its slot.
  const columns = useMemo(() => {
    const cols: Column[] = [
      { key: "#" },
      {
        key: "dateTime",
        name: t("date"),
        type: "datetime",
        mobileView: "topLeft",
      },
      { key: "cardUser", name: t("card_user"), mobileView: "bottomLeft" },
      { key: "cardNumber", name: t("card_no") },
      {
        key: "place",
        name: t("place"),
        mobileView: canManageFuelList ? undefined : "bottomRight",
      },
      { key: "Name", name: t("fuel_type") },
      { key: "quantity", name: t("quantity"), type: "number" },
      { key: "recordType", name: t("record_type"), type: "badge" },
      {
        key: "amount",
        name: t("amount"),
        type: "currency",
        mobileView: "topRight",
      },
    ];

    if (canManageFuelList) {
      cols.push({
        key: "actions",
        name: t("actions"),
        type: "custom",
        mobileView: "bottomRight",
        render: (item: any) => (
          <InlineStack gap="200" blockAlign="center" wrap={isPhone}>
            {item.isFuel ? (
              <Text as="span" variant="bodySm" tone="success">
                {t("in_fuel_list")}
              </Text>
            ) : item.fuelListEntry ? (
              <Button
                size="slim"
                onClick={() => handleAddToFuelList(item)}
                loading={addingId === item.id}
                disabled={addingId !== null && addingId !== item.id}
              >
                {t("add_to_fuel_list")}
              </Button>
            ) : (
              <Text as="span" variant="bodySm" tone="subdued">
                —
              </Text>
            )}
            <Button
              size="slim"
              tone="critical"
              variant="tertiary"
              onClick={() => setRecordToDelete(item)}
              disabled={isDeleting}
            >
              {t("delete_fuel_record")}
            </Button>
          </InlineStack>
        ),
      });
    }

    return cols;
  }, [
    t,
    canManageFuelList,
    addingId,
    handleAddToFuelList,
    isDeleting,
    isPhone,
  ]);

  const showSkeleton = isLoading && records.length === 0;
  const showEmpty = !isLoading && !isError && records.length === 0;

  return (
    <BlockStack gap="400">
      <Card>
        <BlockStack gap="300">
          <InlineStack align="space-between" blockAlign="end" gap="400">
            <div style={{ minWidth: 275 }}>
              <DateRangePicker
                start={range.start}
                end={range.end}
                onChange={(r) => setRange({ start: r.start, end: r.end })}
                label={t("date_range")}
                disableDatesAfter={new Date()}
              />
            </div>
            {totals && (
              <InlineStack gap="400" blockAlign="center">
                <Text as="span" variant="bodySm" tone="subdued">
                  {t("records")}: {totals.count}
                </Text>
                <Text as="span" variant="bodyMd">
                  {t("fuel_records")}:{" "}
                  <Currency
                    value={totals.fuelAmount}
                    isSymbol={true}
                    isDecimal={true}
                  />
                </Text>
                <Text as="span" variant="bodyMd">
                  {t("additional_expenses")}:{" "}
                  <Currency
                    value={totals.additionalAmount}
                    isSymbol={true}
                    isDecimal={true}
                  />
                </Text>
                <Text as="span" variant="bodyMd" fontWeight="bold">
                  {t("total")}:{" "}
                  <Currency
                    value={totals.totalAmount}
                    isSymbol={true}
                    isDecimal={true}
                  />
                </Text>
              </InlineStack>
            )}
          </InlineStack>
        </BlockStack>
      </Card>

      {(actionError || actionSuccess) && (
        <Banner
          tone={actionError ? "critical" : "success"}
          onDismiss={() => {
            setActionError("");
            setActionSuccess("");
          }}
        >
          {actionError || actionSuccess}
        </Banner>
      )}

      <Card>
        {showSkeleton ? (
          <DataTableSkeleton rows={10} columns={columns.length} />
        ) : isError ? (
          <EmptyState
            heading={t("not_fetch", {
              defaultValue: "Could not load transactions",
            })}
            image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
          >
            <Button onClick={load}>
              {t("retry", { defaultValue: "Retry" })}
            </Button>
          </EmptyState>
        ) : showEmpty ? (
          <EmptyState
            heading={t("no_data", { defaultValue: "No transactions found" })}
            image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
          />
        ) : (
          <DataTable
            collection={tableData}
            columns={columns}
            maxBodyHeight={isPhone ? undefined : "calc(100vh - 360px)"}
          />
        )}
      </Card>

      {recordToDelete && (
        <Modal
          open
          title={t("delete_fuel_record_title")}
          onClose={() => setRecordToDelete(null)}
          primaryAction={{
            content: t("delete_fuel_record"),
            destructive: true,
            loading: isDeleting,
            onAction: handleDelete,
          }}
          secondaryActions={[
            {
              content: t("No"),
              disabled: isDeleting,
              onAction: () => setRecordToDelete(null),
            },
          ]}
        >
          <Modal.Section>
            <Text as="p" variant="bodyMd">
              {t("delete_fuel_record_confirmation", {
                date: recordToDelete.dateTime,
                card: recordToDelete.cardNumber,
                amount: Number(recordToDelete.amount || 0).toFixed(2),
              })}
            </Text>
          </Modal.Section>
        </Modal>
      )}
    </BlockStack>
  );
}

export default FuelTransactionsTab;
